import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { AreaItemPack } from '../models/area-item-pack';
import { AreaItem } from '../models/area-item';
import { BookingService } from './booking.service';
import { BookingSlot } from '../models/booking-slot';
import { OrderStateService } from '../../order/services/order-state.service';
import { DayOfWeek } from '../models/day-of-week';
import { OrderTypeEnum } from '../../order/models/order-type.enum';
import { Order } from '../../order/models/order';
import { SingleBookingCache } from '../models/single-booking-cache';
import { BookingSlotHelper } from './booking-slot.helper';
import { OrderLineType } from '../../order/models/order-line-type';
import { EventBookingCache } from '../models/event-booking-cache';

import { LoggerService } from '../../../services/logger/logger.service';
import { PartnerStateService } from '../../partner/services/partner-state.service';
import { ShopSettingsService } from '../../general/services/shop-settings.service';
import { Product } from '../../order/models/product';
import { PriceListStateService } from '../../payment/services/pricelist-state.service';
import { TimeRestrictionService } from './time-restriction.service';
import { EventAreaItemSlot } from '../models/event-area-item-slot';
import { environment } from '../../../../environments/environment';
import { EventEmitter } from '@angular/core';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { ProductStockError } from '../../order/models/product-stock-error';
import { BasketService } from '../../order/services/basket.service';
import { VuConfigurationService } from '../../vu/services/vu-configuration.service';
import { GlobalSettings } from 'src/environments/global-settings';

@Injectable()
export class BookingOrderService {

  readonly messageChange: EventEmitter<any> = new EventEmitter<any>();

  private _areaItemPacks: AreaItemPack[];
  private _mainPageAreaItemPacks: AreaItemPack[];
  private _areaItemPack: AreaItemPack;
  private _areaItems: AreaItem[];
  private _selectedBookingSlots: BookingSlot[];
  private _cachedBookingSlots: BookingSlot[];
  private _selectedBookingDate: Date;
  private _selectedAreaItem: AreaItem;
  private _singleCache = new SingleBookingCache(this);
  private _eventBookingCache = new EventBookingCache(this, this.shopSettingsService);
  private _eventAreaItemSlots: EventAreaItemSlot[];
  private _eventAreaItemBookingSlot: BookingSlot;
  private _selectedPickupPointId: number = null;
  private _selectedPickupPointGroupId: number = null;
  private _stockLocationsIds: number[];

  private readonly _areaItemPackGroupNameKey = 'areaItemPackGroupName';

  constructor(
    private bookingService: BookingService,
    private orderStateService: OrderStateService,
    private logger: LoggerService,
    private partnerStateService: PartnerStateService,
    private shopSettingsService: ShopSettingsService,
    private priceListStateService: PriceListStateService,
    private timeRestrictionService: TimeRestrictionService,
    private basketService: BasketService,
    private vuConfigurationService: VuConfigurationService,
  ) {
  }

  get order(): Order {
    return this.orderStateService.order;
  }

  get singleCache(): SingleBookingCache {
    return this._singleCache;
  }

  get eventCache(): EventBookingCache {
    return this._eventBookingCache;
  }

  get allAreaItemPacks(): AreaItemPack[] {
    return this._areaItemPacks || [];
  }

  get areaItemPacks(): AreaItemPack[] {
    return this.filterAreaItemPacks(this._areaItemPacks, this.areaItemPackGroupName);
  }

  get visibleAreaItemPacks(): AreaItemPack[] {
    const areaItemPacks = this.areaItemPacks.filter(x => !x.showOnHomePage && x.areaItems && x.areaItems.length !== 0 && !x.groupMerge);

    if (!this.shopSettingsService.isVUMode) {
      return areaItemPacks.filter(x => !x.hideOnWebshop);
    }

    return areaItemPacks;
  }

  get bookingGroupNames(): string[] {
    return this.allAreaItemPacks ?
      this.allAreaItemPacks
        .filter(item => !item.showOnHomePage) // ignore events
        .filter(item => !item.groupMerge)
        .map(item => item.groupName)
        .filter(this.onlyUnique) :
      [];
  }

  onlyUnique(value, index, self): boolean {
    return value && self.indexOf(value) === index;
  }

  filterAreaItemPacks(areaItemPacks: AreaItemPack[], groupName: string): AreaItemPack[] {
    if (!areaItemPacks) {
      return [];
    }

    if (groupName) {
      return areaItemPacks.filter(item => item.groupName === groupName && !item.groupMerge);
    }

    return areaItemPacks.filter(item => !item.groupName);
  }

  get areaItems(): AreaItem[] {
    return this._areaItems;
  }

  get areaAreaItemPack(): AreaItemPack {
    return this._areaItemPack;
  }

  get selectedBookingDate(): Date {
    return this._selectedBookingDate;
  }

  get selectedBookingSlots(): BookingSlot[] {
    return this._selectedBookingSlots;
  }

  getFromSelectedBookingSlots(areaId: number): BookingSlot {
    if (!this.selectedBookingSlots || !areaId) {
      return null;
    }

    for (const bookingSlot of this.selectedBookingSlots) {
      const areaBookingSlot = bookingSlot?.getBookingSlot(areaId);
      if (areaBookingSlot) {
        return areaBookingSlot;
      }
    }
    return null;
  }

  getFromSelectedBookingSlotsId(areaId: number): number {
    const bookingSlot = this.getFromSelectedBookingSlots(areaId);
    return bookingSlot ? bookingSlot.id : 0;
  }

  get selectedAreaItem(): AreaItem {
    return this._selectedAreaItem;
  }

  get selectedPickupPointId(): number {
    return this._selectedPickupPointId;
  }

  get selectedPickupPointGroupId(): number {
    return this._selectedPickupPointGroupId;
  }

  set selectedPickupPointGroupId(pickupPointGroupId: number) {
    if (this._selectedPickupPointGroupId === pickupPointGroupId) {
      return;
    }

    this._selectedPickupPointGroupId = pickupPointGroupId;
    this._updateOrderLineFromCache();
    this.messageChange.emit();
  }

  private _updateOrderLineFromCache(): void {
    if (!this.singleCache || !this.singleCache.timeStampsCache || !this.singleCache.ordersCache) {
      return;
    }
    const selectedProductTimeSpan = this.singleCache.timeStampsCache.selectedProductTimeSpan;
    if (!selectedProductTimeSpan) {
      return;
    }
    this.singleCache.ordersCache.updateServiceOrderLinesFromCache(selectedProductTimeSpan.timeSpan);
  }

  set selectedPickupPointId(pickupPointId: number) {
    if (this._selectedPickupPointId === pickupPointId) {
      return;
    }

    this._selectedPickupPointId = pickupPointId;
    this._updateOrderLineFromCache();
  }

  get stockLocationsIds(): number[] {
    return this._stockLocationsIds;
  }

  get mainPageAreaItemPacks(): AreaItemPack[] {
    return this._mainPageAreaItemPacks;
  }

  get eventAreaItemSlots(): EventAreaItemSlot[] {
    return this._eventAreaItemSlots;
  }

  get eventAreaItemBookingSlot(): BookingSlot {
    return this._eventAreaItemBookingSlot;
  }

  get mergeAreaAreaItemPacks(): AreaItemPack[] {
    if (!this.areaAreaItemPack) {
      return null;
    }

    return this._areaItemPacks.filter(pack => this.areaAreaItemPack.groupName == pack.groupName && pack.groupMerge && this.areaAreaItemPack.id != pack.id);
  }

  public getBookingSlots(date: Date): Observable<BookingSlot[]> {
    return of(true).pipe(
      switchMap(_ => {
        if (!this.areaAreaItemPack) {
          this._cachedBookingSlots = [];
          return of(null);
        }

        if (!this.areaAreaItemPack.hasEvents) {
          const mergeAreaAreaItemPacks = this.mergeAreaAreaItemPacks;
          if (mergeAreaAreaItemPacks) {
            return this.bookingService.getBookingSlots(this.areaAreaItemPack.id, mergeAreaAreaItemPacks.map(item => item.id), date).pipe(
              map(slots => {
                let areaItems = [...this.areaAreaItemPack.areaItems];
                for (let mergeAreaAreaItemPack of mergeAreaAreaItemPacks) {
                  if (mergeAreaAreaItemPack.areaItems) {
                    areaItems = areaItems.concat(mergeAreaAreaItemPack.areaItems);
                  }
                }

                this._areaItems = areaItems;

                if (this._areaItems) {
                  const minPrice = Math.min(...this._areaItems.map(x => x.price));
                  slots.forEach(x => x.minPrice = minPrice);
                }

                slots = slots.filter(item => this.isAvailableBookingSlot(item, date, this._areaItems));

                let uniqueSlots = [];

                for (let slot of slots) {
                  let timeSlotExists = uniqueSlots.find(o => o.fromTime === slot.fromTime && o.toTime === slot.toTime);
                  if (timeSlotExists) {
                    if (timeSlotExists.mergeSlots == null) {
                      timeSlotExists.mergeSlots = [slot];
                      continue;
                    }

                    if (!timeSlotExists.mergeSlots.includes(slot)) {
                      timeSlotExists.mergeSlots.push(slot);
                    }
                  } else {
                    uniqueSlots.push(slot);
                  }
                }

                this._cachedBookingSlots = uniqueSlots;
                return uniqueSlots;
              }),
            );
          }

          return this.bookingService.getBookingSlots(this.areaAreaItemPack.id, null, date).pipe(
            map(slots => {
              if (this._areaItems) {
                const minPrice = Math.min(...this._areaItems.map(x => x.price));
                slots.forEach(x => x.minPrice = minPrice);
              }

              slots = slots.filter(item => this.isAvailableBookingSlot(item, date, this._areaItems));
              this._cachedBookingSlots = slots;
              return slots;
            }),
          );
        }

        if (!this.areaAreaItemPack.areaItems) {
          return of(null);
        }

        return this.selectedAreaItem ?
          this.bookingService.getEventBookingSlots(
            this.selectedAreaItem,
            date
          ) :
          of(null);
      })
    );

  }

  get enabledDaysOfWeek(): Array<number> {
    return this.timeRestrictionService.daysOfWeek;
  }

  isDateEnabled(date: Date): boolean {
    return this.timeRestrictionService.isDateValid(date);
  }

  filterBookingSlots(bookingSlots: BookingSlot[], day: DayOfWeek): BookingSlot[] {
    return this.timeRestrictionService.filterSelectableBookingSlots(bookingSlots, day, this.areaItems);
  }

  private _checkBookingSlotsValid(
    bookingSlots: BookingSlot[],
    timeMinutes: number,
    selectedProductsCount: number
  ) {

    const validSlots = bookingSlots;

    if (!this.selectedBookingSlots || !validSlots) {
      return false;
    }

    return this.selectedBookingSlots.some(selectedBookingSlot => validSlots.find(x => x.id === selectedBookingSlot?.id) != null);
  }

  isBookingSlotCapacityValid(
    timeMinutes: number,
    selectedProductsCount: number)
    : Observable<boolean> {

    return of(true).pipe(
      switchMap(_ => {
        if (this.selectedBookingDate === null || this.selectedBookingSlots === null) {
          return of(false);
        }

        if (this.eventAreaItemBookingSlot) {
          return of(this._checkBookingSlotsValid(
            [this.eventAreaItemBookingSlot],
            timeMinutes,
            selectedProductsCount
          ));
        }

        if (this._cachedBookingSlots) {
          return of(this._checkBookingSlotsValid(
            this._cachedBookingSlots,
            timeMinutes,
            selectedProductsCount
          ));
        } else {
          return this.getBookingSlots(this.selectedBookingDate).pipe(
            map(
              bookingSlots => {
                if (!bookingSlots || !bookingSlots.length) {
                  return false;
                } else {
                  this._cachedBookingSlots = bookingSlots;
                  return this._checkBookingSlotsValid(
                    this._cachedBookingSlots,
                    timeMinutes,
                    selectedProductsCount
                  );
                }
              },
            ),
          );
        }
      })
    );

  }

  get isSingleOrder(): boolean {
    return this.order && this.order.orderType === OrderTypeEnum.Single;
  }

  get isEventOrder(): boolean {
    return this.order && (this.order.orderType === OrderTypeEnum.Birthday || this.order.orderType === OrderTypeEnum.Group);
  }

  get isEventSlotOrder(): boolean {
    return this.order && this.order.orderType === OrderTypeEnum.EventSlot;
  }

  get orderType(): OrderTypeEnum {
    if (this.selectedAreaItem && this.selectedAreaItem.isBirthday) {
      return OrderTypeEnum.Birthday;
    }

    if (this.selectedAreaItem && this.selectedAreaItem.isGroup) {
      return OrderTypeEnum.Group;
    }

    if (this.eventAreaItemBookingSlot) {
      return OrderTypeEnum.EventSlot;
    }

    return OrderTypeEnum.Single;
  }

  get isBirthday() {
    return this.selectedAreaItem && this.selectedAreaItem.isBirthday;
  }

  get isGroup() {
    return this.selectedAreaItem && this.selectedAreaItem.isGroup;
  }

  get isCashPayment() {
    return this.selectedAreaItem && this.selectedAreaItem.isCashPayment;
  }

  get isEventTypeOrder() {
    return this.isBirthday || this.isGroup;
  }

  initializeOrderService(
    areaItemPack: AreaItemPack,
    selectedAreaItem: AreaItem,
  ): Observable<boolean> {

    return of(true).pipe(
      switchMap(_ => {
        if (!areaItemPack) {
          return of(false);
        }

        if (this._areaItemPack === areaItemPack) {
          return of(true);
        }

        this._areaItemPack = areaItemPack;

        if (selectedAreaItem) {
          this._selectedAreaItem = selectedAreaItem;
        }

        return of(_).pipe(
          switchMap(__ => this.orderStateService.initialize(this.orderType)),
          tap(__ => { this.orderStateService.order.packId = this._areaItemPack.id; }),
          switchMap(__ => this.reloadPackData(this._areaItemPack.id)),
        );
      })
    );
  }

  reloadPackData(packId: number): Observable<boolean> {
    return of(packId).pipe(
      switchMap(_ => this.bookingService.getAreaItems(packId)),
      tap(areaItems => {
        this._areaItems = areaItems;
      }),
      switchMap(_ => this.reloadTimeRestrictions(packId)),
    );
  }

  reloadTimeRestrictions(packId: number): Observable<boolean> {
    return of(packId).pipe(
      tap(_ => {
        this.timeRestrictionService.clear();
      }),
      switchMap(_ => this.bookingService.getAreaRestrictions(packId, this.mergeAreaAreaItemPacks?.map(pack => pack.id))),
      map(restrictions => {
        this.timeRestrictionService.initialize(restrictions);
        this.messageChange.emit();
        return true;
      }),
    );
  }

  initializeEventSlotOrderService(
    selectedAreaItem: AreaItem,
    eventAreaItemBookingSlot,
  ): Observable<boolean> {
    return of(true).pipe(
      switchMap(_ => {
        if (selectedAreaItem) {
          this._selectedAreaItem = selectedAreaItem;
          this._areaItems = [selectedAreaItem];
        }

        if (eventAreaItemBookingSlot) {
          this._eventAreaItemBookingSlot = eventAreaItemBookingSlot;
        }

        return this.orderStateService.initialize(this.orderType);
      }),
      tap(_ => {
        if (this._areaItemPack) {
          this.orderStateService.order.packId = this._areaItemPack.id;
        }
      }),
    );
  }

  setSelectedBookingDateAndSlots(selectedBookingDate: Date, selectedBookingSlots: BookingSlot[], allowNull = false) {
    if (selectedBookingSlots || allowNull) {
      this._selectedBookingSlots = selectedBookingSlots;
    }

    if (selectedBookingDate || allowNull) {
      this._selectedBookingDate = selectedBookingDate;
    }

    if (this.isEventTypeOrder) {
      this.eventCache.update();
    }

    this.messageChange.emit();
  }

  reinitialize(): Observable<boolean> {
    return of(true).pipe(
      tap(_ => {
        this.logger.info(`BookingOrderService: reinitialize.`);

        this._areaItemPacks = null;
        this._mainPageAreaItemPacks = null;
      }),
      switchMap(_ => this.initialize()),
      tap(_ => this.logger.info(`BookingOrderService: reinitialize done.`)),
      catchError(error => {
        this.logger.info(`BookingOrderService: reinitialize failed ${error}.`);
        return throwError(error);
      }),
    );
  }

  reset() {

    this.logger.info(`BookingStateService: reset.`);

    this._areaItemPack = null;
    this._areaItems = null;
    this._selectedBookingSlots = null;
    this._selectedBookingDate = null;
    this._selectedAreaItem = null;
    this._cachedBookingSlots = null;
    this._eventAreaItemSlots = null;
    this._eventAreaItemBookingSlot = null;
    this.timeRestrictionService.clear();
    this._singleCache.reset();
    this._eventBookingCache.reset();
    this._selectedPickupPointId = null;
    this._selectedPickupPointGroupId = null;
    this._stockLocationsIds = null;
  }

  resetSettings(): Observable<boolean> {
    return of(true).pipe(
      tap(_ => {
        this.reset();
      }),
      switchMap(_ => {
        return this.reloadVuConfiguration();
      }),
    );
  }

  initialize(): Observable<boolean> {

    return of(true).pipe(
      tap(_ => {
        this.logger.info(`BookingStateService: initialize.`);
      }),
      switchMap(_ => {
        return this.resetSettings();
      }),
      switchMap(_ => {
        if (this._areaItemPacks) { // && this._shopSettings
          this.logger.info(`BookingStateService: initialize done. areaItemPacks were cached.`);
          return of(true);
        }

        this.logger.info(`BookingStateService: initialize receiving areaItemPacks.`);

        return this.bookingService.getAreaItemPacks().pipe(
          tap(areaItemPacks => {
            this.logger.info(`BookingStateService: initialize areaItemPacks(${areaItemPacks.length}) received.`);

            this._areaItemPacks = areaItemPacks;
            this._mainPageAreaItemPacks = areaItemPacks ? areaItemPacks.filter(x => x.showOnHomePage) : [];
          }),
          catchError(error => {
            this.logger.info(`BookingStateService: initialize areaItemPacks receive error ${error}.`);
            return throwError(error);
          }),
          switchMap(() => {
            return this.shopSettingsService.initialize().pipe(
              map(() => {
                this.logger.info(`BookingStateService: initialize shop settings received.`);
                // this._shopSettings = this.shopSettingsService.shopSettings;
                return true;
              }),
              catchError(error => {
                this.logger.info(`BookingStateService: initialize shop settings receive error ${error}.`);
                return throwError(error);
              }),
            );
          }),
        );
      }),
    );
  }

  initializeEventsAreaItemSlots(): Observable<boolean> {
    return of(true).pipe(
      tap(_ => {
        this.logger.info(`BookingStateService: initializeEventsAreaItemSlots.`);
      }),
      switchMap(_ => {
        return this.resetSettings();
      }),
      switchMap(_ => {
        this.logger.info(`BookingStateService: initializeEventsAreaItemSlots receiving eventAreaItemSlots.`);
        return this.bookingService.getEventAreaItemSlots().pipe(
          catchError(error => {
            this.logger.info(`BookingStateService: initialize eventAreaItemSlots receive error ${error}.`);
            return throwError(error);
          }),
        );
      }),
      switchMap(eventAreaItemSlots => {
        this.logger.info(`BookingStateService: initialize eventAreaItemSlots(${eventAreaItemSlots.length}) received.`);
        this._eventAreaItemSlots = eventAreaItemSlots;
        if (this.shopSettingsService.shopSettings) {
          this.logger.info(`BookingStateService: initialize done. shopSettings were cached.`);
          return of(true);
        }
        this.shopSettingsService.initialize().pipe(
          map(shopSettings => {
            this.logger.info(`BookingStateService: initialize shop settings received.`);
            // this._shopSettings = this.shopSettingsService.shopSettings;
            return of(true);
          },
            catchError(error => {
              this.logger.info(`BookingStateService: initialize shop settings receive error ${error}.`);
              return throwError(error);
            }),
          ));
      })
    );
  }

  reloadVuConfiguration(): Observable<boolean> {
    return of(true).pipe(
      switchMap((_) => {
        if (!this.vuConfigurationService.isAvailable) {
          return of(_)
        }
        return this.vuConfigurationService.vuConfiguration.pipe(
          map((vuConfiguration) => {
            this.logger.info(`BookingStateService: relaod vu configuration received.`);
            this._stockLocationsIds = vuConfiguration.stockLocationsIds;
            return _;
          }),
          catchError(error => {
            this.logger.info(`BookingStateService: relaod vu configuration receive error ${error}.`);
            return throwError(error);
          }),
        );
      }),
    );
  }

  createOrder(): Observable<Order> {

    return of(true).pipe(
      switchMap(_ => {
        if (this.orderType === OrderTypeEnum.Birthday && this.eventCache && this.eventCache.guestOfHonor) {
          return this.partnerStateService.createChild(this.eventCache.guestOfHonor).pipe(
            map(child => {
              this.orderStateService.order.guestOfHonorId = child.id;
            }),
          );
        } else {
          return of(_);
        }
      }),
      switchMap(_ => this._createOrder()),
      catchError(error => {
        if (error instanceof ProductStockError && this._areaItems) {
          for (const productsStockInformation of error.productsStockInformation) {
            if (productsStockInformation && productsStockInformation.available) {
              continue;
            }
            this.changeAvailableProductQuantity(productsStockInformation.productId, productsStockInformation.availableQuantity, productsStockInformation.locationId);
          }
        }
        return throwError(error);
      }),
    );
  }

  changeAvailableProductQuantity(productId: number, quantity: number, locationId: number) {
    if (this._areaItems == null) {
      return;
    }

    const areaItem = this._areaItems.find(item => item.productId === productId);
    if (!areaItem) {
      return;
    }

    areaItem.setAvailableQuantity(quantity, locationId);
  }

  saleToInvoice(orderId: number): Observable<boolean> {
    return this.orderStateService.saleToInvoice(orderId);
  }

  cancelVUActiveOrder(): Observable<boolean> {
    return of(true).pipe(
      switchMap(_ => {
        if (this.shopSettingsService.isVUMode && this.orderStateService.sessionOrderId) {
          this.orderStateService.sessionPaymentProviderId = -1;
          if (this.orderStateService.sessionOrderId === -1) {
            this.orderStateService.sessionOrderId = 0;
            return of(true);
          }
          return this.cancelOrder(this.orderStateService.sessionOrderId).pipe(
            tap(__ => { this.orderStateService.sessionOrderId = 0; }),
            catchError(error => {
              this.orderStateService.sessionOrderId = 0;
              this.logger.info(`BookingOrderService: failed - cancelOrder: ${error.msg || error}.`);
              return of(false);
            }),
          );
        }
        return of(_);
      })
    );
  }

  cancelOrder(orderId: number): Observable<any> {
    return this.orderStateService.cancelOrder(orderId);
  }

  private _createOrder(): Observable<Order> {
    return this.orderStateService.createOrder().pipe(
      switchMap(order => this.initialize().pipe(map(_ => order))),
    );
  }

  get maximalDurationTimeOfProduct(): number {
    const order = this.order;
    if (order == null || order.orderLines == null) {
      return 0;
    }
    const orderLines = order.orderLines;
    const productAreaItemIds = orderLines.filter(
      orderLine => (orderLine.orderLineType === OrderLineType.SingleProduct) || (orderLine.orderLineType === OrderLineType.EventProduct)
    ).map(orderLine => orderLine.productId);
    if (productAreaItemIds.length === 0) {
      return 0;
    }

    const productAreaItems = this.areaItems.filter(
      areaItem => productAreaItemIds.indexOf(areaItem.productId) !== -1
    );
    if (!productAreaItems || productAreaItems.length === 0) {
      return 0;
    }
    const durationTimeOfProducts: number[] = productAreaItems.map(productAreaItem => productAreaItem.minutes);
    if (durationTimeOfProducts == null) {
      return 0;
    }
    return Math.max.apply(Math, durationTimeOfProducts);
  }

  get additionalProductsIds(): number[] {
    const productsIds = new Array<number>();
    const order = this.order;

    if (!this.areaItems) {
      return productsIds;
    }
    this.areaItems.forEach(
      areaItem => {
        if (!areaItem || !areaItem.additionalProductIds || !order.getOrderLineByProductId(areaItem.productId)) {
          return;
        }
        areaItem.additionalProductIds.forEach(
          productId => {
            if (productsIds.indexOf(productId) === -1) {
              productsIds.push(productId);
            }
          });
      }
    );

    return productsIds;
  }

  get automaticAdditionalProductsIds(): number[] {
    const productsIds = new Array<number>();
    const order = this.order;

    if (!this.areaItems) {
      return productsIds;
    }
    this.areaItems.forEach(
      areaItem => {
        if (!areaItem || !areaItem.automaticAdditionalProductsIds || !order.getOrderLineByProductId(areaItem.productId)) {
          return;
        }
        areaItem.automaticAdditionalProductsIds.forEach(
          productId => {
            if (productsIds.indexOf(productId) === -1) {
              productsIds.push(productId);
            }
          });
      }
    );

    return productsIds;
  }

  get isAdditionalProductsExist(): boolean {
    const productsIds = this.additionalProductsIds;
    return productsIds != null && productsIds.length !== 0;
  }

  get isAutomaticAdditionalProductsExist(): boolean {
    const productsIds = this.automaticAdditionalProductsIds;
    return productsIds != null && productsIds.length !== 0;
  }

  getAutomaticAdditionalProductByIds(automaticAdditionalProductsIds): Observable<Product[]> {
    if (automaticAdditionalProductsIds) {
      return this.orderStateService.getAutomaticAdditionalProductByIds(automaticAdditionalProductsIds);
    } else {
      return of(null);
    }
  }

  getPriceListStateService() {
    return this.priceListStateService;
  }

  computePriceByProduct(product: Product, qty: number, selectedBookingSlots?: BookingSlot[]) {
    selectedBookingSlots = selectedBookingSlots ? selectedBookingSlots : this.selectedBookingSlots;
    const order = this.order;
    const partner = this.partnerStateService.loggedInPartner;
    const price = this.priceListStateService.computePrice(product, qty, partner, order, null, selectedBookingSlots);
    return price;
  }

  computePriceByProductVals(productVals: {}, selectedBookingSlots?: BookingSlot[]) {
    selectedBookingSlots = selectedBookingSlots ? selectedBookingSlots : this.selectedBookingSlots;
    const order = this.order;
    const partner = this.partnerStateService.loggedInPartner;
    const price = this.priceListStateService.computePrice(null, null, partner, order, productVals, selectedBookingSlots);
    return price;
  }

  computePriceByAreaItem(areaItem: AreaItem, count: number): number {
    const productVals = {
      'id': areaItem.productId,
      'price': areaItem.price,
      'qty': count,
      // 'categId': areaItemTemp.categId,
      'productPriceSchedules': areaItem.productPriceSchedules,
    };
    const price = this.computePriceByProductVals(productVals);
    return price;
  }

  filterAreaItemsByBookingSlots(areaItems: AreaItem[], date: Date, bookingSlots: BookingSlot[]): AreaItem[] {
    if (!date) {
      return [];
    }


    const filteredBookingSlots = this.timeRestrictionService.filterSelectableBookingSlots(bookingSlots, date.getDay(), areaItems);
    if (!filteredBookingSlots || filteredBookingSlots.length === 0) {
      return [];
    }

    const areaItemsDict = {};
    for (const areaItem of areaItems) {
      areaItemsDict[areaItem.id] = areaItem;
    }

    const areaItemsIds = new Set<number>();
    for (const filteredBookingSlot of filteredBookingSlots) {
      if (!filteredBookingSlot || !filteredBookingSlot.availableAreaItemsIds) {
        continue;
      }

      filteredBookingSlot.availableAreaItemsIds
        .filter(areaItemId => this.isAvailableAreaItemByRestrictionsTime(areaItemsDict[areaItemId], date, filteredBookingSlot))
        .forEach(item => areaItemsIds.add(item));
    }

    return areaItems.filter(areaItem => areaItemsIds.has(areaItem.id));
  }

  isAvailableBookingSlot(bookingSlot: BookingSlot, date: Date, areaItems: AreaItem[]): boolean {
    if (!bookingSlot || !areaItems) {
      return false;
    }

    for (const areaItem of areaItems) {
      if(areaItem.areaId != bookingSlot.areaId) {
        continue;
      }

      if (this.isAvailableAreaItem(areaItem, date, bookingSlot)) {
        return true;
      }
    }
    return false;
  }

  private isAvailableAreaItemByRestrictionsTime(areaItem: AreaItem, date: Date, bookingSlot: BookingSlot): boolean {
    if (!this.isAvailableAreaItem(areaItem, date, bookingSlot)) {
      return false;
    }
    return this.timeRestrictionService.isAvailableAreaItemByDateAndSlot(areaItem, date, bookingSlot);
  }

  private isAvailableAreaItem(areaItem: AreaItem, date: Date, bookingSlot: BookingSlot): boolean {
    if (!environment.globalSettings.isToday(date)) {
      return true;
    }

    const dateTimeNow = GlobalSettings.DateNow;
    const dateTime = dateTimeNow.getHours() + (dateTimeNow.getMinutes() / 60);

    if (bookingSlot.fromTime >= dateTime) {
      return true;
    }

    if (!areaItem.openedBooking) {
      return false;
    }

    const areaItemToTime = bookingSlot.fromTime + areaItem.minutes / 60;
    return areaItemToTime > dateTime;
  }

  get areaItemPackGroupName(): string {
    return sessionStorage.getItem(this._areaItemPackGroupNameKey);
  }

  set areaItemPackGroupName(value: string) {
    if (value) {
      sessionStorage.setItem(this._areaItemPackGroupNameKey, value + '');
    } else {
      sessionStorage.removeItem(this._areaItemPackGroupNameKey);
    }
  }

  isShowProductConfigurationPage(): boolean {
    let isShowPage = false;
    const order = this.order;
    if (!order) {
      return isShowPage;
    }

    const orderLines = order.orderLines;
    const areaItems = this.areaItems;
    if ((!orderLines || orderLines.length < 1) || (!areaItems || areaItems.length < 1)) {
      return isShowPage;
    }

    for (const orderLine of orderLines) {
      isShowPage = areaItems.some(item =>
        (item.productId === orderLine.productId) &&
        (item.isBirthdayRequired || item.isPartnerRequired || item.isAddressRequired || item.isPhoneRequired || item.isEmailRequired));
      if (isShowPage) {
        break;
      }
    }

    return isShowPage;
  }

  get showQuantityAvailableTicketsOnMainPage(): boolean {
    return this._areaItemPacks ? this._areaItemPacks.some(item => item.showQuantityAvailableTickets) : false;
  }

  get isMultiOrderMode(): boolean {
    return this.shopSettingsService.shopSettings && this.shopSettingsService.shopSettings.multiOrderMode;
  }

  reinitializeFromOrder(): Observable<any> {
    return of(true).pipe(
      switchMap(_ => {
        if (!this.areaItemPacks || this.areaItemPacks.length === 0) {
          return this.reinitialize();
        }
        return of(false);
      }),
      switchMap(reinitialized => {
        this.singleCache.ordersCache.resetIsCacheLinesQtyCalculatedForAllChachedOders();
        if (this.orderStateService.order && this.orderStateService.order.packId) {
          const packId = this.orderStateService.order.packId;
          this._areaItemPack = this.areaItemPacks.find(item => item.id === packId);
          if (this._areaItemPack && reinitialized) {
            this._areaItems = this._areaItemPack.areaItems;
            return this.reloadTimeRestrictions(packId);
          }
          return this.reloadPackData(packId);
        }
        return of(true);
      }),
    );
  }

  clearSelectedDateOrder(): void {
    if (!this.selectedBookingDate || !this.order) {
      return;
    }

    const lines = this.basketService.getOrderLines(this.order, this.selectedBookingDate);
    if (!lines) {
      return;
    }
    for (const line of lines) {
      line.quantity = 0;
      this.order.deleteOrderLine(line);
    }

    this.singleCache.ordersCache.resetCacheLinesQuantity();
    // this.singleCache.ordersCache.resetIsCacheLinesQtyCalculatedForAllChachedOders();
    this.orderStateService.orderSaveToLocalStorage();
    this.singleCache.updateInvalidReason();
    this.messageChange.emit();
  }

  get isPartnerHaveMaxOrderCount(): boolean {
    if (this.partnerStateService && this.shopSettingsService && this.shopSettingsService.webshopPartnerMaxOrderCount) {
      const partner = this.partnerStateService.loggedInPartner;
      if (partner && partner.webshopOrderCount >= this.shopSettingsService.webshopPartnerMaxOrderCount) {
        return true;
      }
    }
    return false;
  }
}
